import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref, shallowReactive } from 'vue';

import { isNonEmptyString } from '@nora-monorepo/shared';
import type { NoraWebViewUpdateMapSources } from '@nora-monorepo/ui-shared';
import { NoraWebViewEventPattern, useAppBridgeStore, useWebViewStore } from '@nora-monorepo/ui-shared';

import { getDefaultMapCenter, MapSetup } from '#nora-ui/map/map/MapSetup';
import { NoraMap } from '#nora-ui/map/map/NoraMap';
import { useMapPersistenceStore } from '#nora-ui/map/stores/map-persistence-store';
import type { MapConfig, MapConfigOverlayMap } from '#nora-ui/map/types/map.types';

export const useMapStore = defineStore('mapStore', () => {
  const mapSetup = MapSetup.getInstance();
  const appBridgeStore = useAppBridgeStore();

  const isBootstrapped = ref(false);

  const mapInstances = useLocalStorage<MapConfig[]>('mapInstances', []);
  const maps = shallowReactive<NoraMap[]>([]);

  const mapCount = computed(() => mapInstances.value.length);

  /**
   * Returns the current base map id if it is still one of the configured base maps,
   * otherwise falls back to the default.
   *
   * Needed because an admin may have removed a base map between sessions, which would
   * leave a persisted `MapConfig` in localStorage pointing at an id that no longer exists.
   *
   * Can return `undefined` when no base maps are configured at all.
   */
  function reconcileBaseMap(currentId: string | undefined): string | undefined {
    const validBaseMaps = mapSetup.getBaseMaps();
    const isStillValid = currentId !== undefined && validBaseMaps.some(baseMap => baseMap.id === currentId);

    if (isStillValid) {
      return currentId;
    }

    return mapSetup.getDefaultBaseMapId(validBaseMaps);
  }

  /**
   * Rebuilds the overlay list against the currently-configured overlays:
   *  - iterates `mapSetup.getOverlayMaps()` so the result follows the admin-configured order
   *  - preserves the user's persisted visibility toggle per id (falls back to the
   *    configured default if the id wasn't persisted yet)
   *  - refreshes metadata (`name`) from the current config, so admin renames take effect
   *
   * Returns the input array by reference when nothing changed, so callers can detect
   * a no-op via identity (`===`) instead of a deep comparison.
   */
  function reconcileOverlayMaps(current: MapConfigOverlayMap[]): MapConfigOverlayMap[] {
    const available = mapSetup.getOverlayMaps();
    const currentById = new Map(current.map(overlay => [overlay.id, overlay]));

    const reconciled: MapConfigOverlayMap[] = available.map((overlay) => {
      const persisted = currentById.get(overlay.id);

      return {
        id: overlay.id,
        visible: persisted?.visible ?? overlay.visible ?? false,
        name: overlay.name ?? overlay.id,
      };
    });

    const overlaysMatchCurrent = reconciled.every((overlay, index) => {
      const existing = current[index];

      return existing !== undefined
        && existing.id === overlay.id
        && existing.visible === overlay.visible
        && existing.name === overlay.name;
    });

    const isUnchanged = reconciled.length === current.length && overlaysMatchCurrent;

    if (isUnchanged) {
      return current;
    }

    return reconciled;
  }

  /**
   * Reconciles a single persisted `MapConfig` against the current map settings.
   *
   * Returns the same instance by reference when neither the base map nor the overlays
   * needed to change — this lets `syncMapInstancesWithSettings` distinguish rewritten
   * instances from untouched ones with a single `!==` check, without a deep compare.
   */
  function reconcileInstance(instance: MapConfig): MapConfig {
    const baseMap = reconcileBaseMap(instance.baseMap);
    const overlayMaps = reconcileOverlayMaps(instance.overlayMaps ?? []);

    if (baseMap === instance.baseMap && overlayMaps === instance.overlayMaps) {
      return instance;
    }

    return { ...instance, baseMap, overlayMaps };
  }

  /**
   * Reconciles every persisted map instance against the current map settings.
   *
   * NOTE: this only updates the localStorage-backed `mapInstances` array. Live `NoraMap`
   * instances were constructed with `ref(mapInstance)` at bootstrap and still hold the
   * OLD object, so replacing entries here does not propagate to the maps on screen —
   * `syncLiveMapsFromInstances` is responsible for that.
   */
  function syncMapInstancesWithSettings(): void {
    mapInstances.value = mapInstances.value.map(reconcileInstance);
  }

  /**
   * Pushes reconciled base map / overlay config from `mapInstances` onto the running
   * `NoraMap` refs and rebuilds each map's layer stack.
   *
   * Needed because `syncMapInstancesWithSettings` replaces entries in `mapInstances.value`
   * via `.map(...)`, but each `NoraMap` holds a ref to the ORIGINAL object captured at
   * bootstrap. Without copying the reconciled fields back onto that captured ref, the
   * stored config and the on-screen map would silently diverge.
   *
   * Only `baseMap` and `overlayMaps` are mirrored across because those are the only
   * fields the reconciler touches. If the reconciler ever starts writing additional
   * fields, they must be added here too
   */
  function syncLiveMapsFromInstances(): void {
    for (const map of maps) {
      const instance = mapInstances.value.find(mapInstance => mapInstance.id === map.getId());

      if (!instance) {
        continue;
      }

      // Mutate the captured ref in place — see function-level doc for why we can't
      // just replace the object. Vue picks up these property assignments reactively.
      const currentMapConfig = map.ref.getMapConfig();
      currentMapConfig.baseMap = instance.baseMap;
      currentMapConfig.overlayMaps = instance.overlayMaps;

      map.layers.recalculateLayers();
    }
  }

  /**
   * Applies a runtime change to map settings (e.g. an admin saving new configuration in
   * the map-settings admin panel) across the three layers that each hold their own copy
   * of the data:
   *
   *   1. `MapSetup` singleton    — rebuild base/overlay tables from the config store
   *   2. `mapInstances` storage  — reconcile each persisted `MapConfig` against those tables
   *   3. live `NoraMap` refs     — push reconciled values onto the captured refs and
   *                                rebuild the OpenLayers layer stack
   *
   * The order matters: each step reads from the output of the previous one.
   */
  function applyMapSettingsRefresh(): void {
    mapSetup.refreshFromSettings();
    syncMapInstancesWithSettings();
    syncLiveMapsFromInstances();
  }

  function normalizeMapInstancesCenter(): void {
    mapInstances.value = mapInstances.value.map((mapInstance) => {
      const center = mapInstance.center;

      if (typeof center.latitude === 'number' && typeof center.longitude === 'number') {
        return mapInstance;
      }

      return {
        ...mapInstance,
        center: { ...getDefaultMapCenter() },
      };
    });
  }

  /**
   * Migrates persisted `MapConfig` entries from the legacy single-`operationId` shape to
   * the `operations[]` + `activeOperationId` shape. Configs persisted before the
   * multi-operation refactor have no `operations` array, which crashes every consumer of
   * `map.ref.operationIds` / `map.ref.visibleOperationIds`.
   */
  function normalizeMapInstancesOperations(): void {
    mapInstances.value = mapInstances.value.map((mapInstance) => {
      if (Array.isArray(mapInstance.operations)) {
        return mapInstance;
      }

      const { operationId: legacyOperationId, ...migratedInstance }
        = mapInstance as MapConfig & { operationId?: string };

      if (!isNonEmptyString(legacyOperationId)) {
        return { ...migratedInstance, operations: [] };
      }

      return {
        ...migratedInstance,
        operations: [{ id: legacyOperationId, visible: true }],
        activeOperationId: legacyOperationId,
      };
    });
  }

  function goToCoordinatesInMap(latitude: number, longitude: number, zoom?: number, operationId?: string | null): void {
    const map = (isNonEmptyString(operationId)
      ? maps.find(map => map.ref.hasOperation(operationId))
      : undefined) ?? maps[0];

    if (map === undefined) {
      return;
    }

    map.setCenter({
      latitude,
      longitude,
      zoom,
    });
  }

  function bootstrap() {
    mapSetup.refreshFromSettings();
    normalizeMapInstancesCenter();
    normalizeMapInstancesOperations();
    syncMapInstancesWithSettings();

    if (mapInstances.value.length === 0) {
      addMap();
    }
    else {
      for (const mapInstance of mapInstances.value) {
        const mapConfigRef = ref(mapInstance);
        const map = new NoraMap(mapConfigRef);

        maps.push(map);
      }
    }

    listenToWebViewEvents();

    appBridgeStore.nora.registerHandler(
      'goToCoordinates',
      goToCoordinatesInMap,
    );

    isBootstrapped.value = true;
  }

  function addMap(operationId?: string) {
    const mapConfig = mapSetup.createNewMapConfig(operationId);
    const mapConfigRef = ref(mapConfig);
    const map = new NoraMap(mapConfigRef);

    maps.push(map);
    mapInstances.value.push(mapConfigRef.value);
  }

  function removeMap(id: string) {
    const mapsIndex = maps.findIndex(map => map.getId() === id);
    const instancesIndex = mapInstances.value.findIndex(map => map.id === id);

    if (mapsIndex !== -1) {
      const map = maps[mapsIndex];
      map.destroy();
      maps.splice(mapsIndex, 1);
    }

    if (instancesIndex !== -1) {
      mapInstances.value.splice(instancesIndex, 1);
    }

    // Remove persisted state for this map
    useMapPersistenceStore().removeMapState(id);
  };

  function getMapById(id: string): NoraMap | undefined {
    return maps.find(map => map.getId() === id);
  }

  function getMapByIdOrThrow(id: string): NoraMap {
    const map = getMapById(id);

    if (!map) {
      throw new Error(`Map with id ${id} not found`);
    }

    return map;
  }

  function getMapByOperationId(operationId: string): NoraMap | undefined {
    // First match: more than one map may show the same operation in the multi-map grid.
    return maps.find(map => map.ref.hasOperation(operationId));
  }

  /**
   * The active operation of the first map that has one. For callers that need an
   * operation context without a specific map in hand (e.g. creating a draft draw layer).
   * Not every map has an active operation, so this can return `undefined`.
   */
  function getActiveOperationId(): string | undefined {
    return maps
      .map(map => map.ref.activeOperationId)
      .find(operationId => isNonEmptyString(operationId));
  }

  function getMapByOperationIdOrThrow(operationId: string): NoraMap {
    const map = getMapByOperationId(operationId);

    if (!map) {
      throw new Error(`Map with operation id ${operationId} not found`);
    }

    return map;
  }

  function listenToWebViewEvents() {
    const webViewStore = useWebViewStore();

    webViewStore.addNativeLayerEventListener(
      NoraWebViewEventPattern.WEBVIEW_UPDATE_MAP_SOURCES,
      (event: CustomEvent<NoraWebViewUpdateMapSources>) => {
        event.detail.data.mapSources.forEach((mapSource) => {
          // Add the offline map to the map setup, so that potentially new maps created later can use it
          mapSetup.addOfflineMap(mapSource);
        });
      },
    );
  }

  return {
    isBootstrapped,
    mapInstances,
    mapCount,
    maps,
    bootstrap,
    addMap,
    removeMap,
    getMapById,
    getMapByIdOrThrow,
    getMapByOperationId,
    getMapByOperationIdOrThrow,
    getActiveOperationId,
    applyMapSettingsRefresh,
  };
});
